import { track } from '@eventbrite/datalayer-library';
import {
    initializeSdk,
    logEvent,
    StatsigConfiguration,
    updateUser,
} from '@eventbrite/statsig';
import { Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { CHECKOUT_FORM_NAME } from '../constants';
import { HeapEventName } from '../constants/heap';
import { STATSIG_EVENT_NAMES } from '../constants/statsig';
import {
    EB_MARKETING_OPT_IN_FIELD,
    ORG_MARKETING_OPT_IN_FIELD,
} from '../containers/checkout/constants';
import { PaymentMethodType } from '../models/paymentMethods/enums';
import { getSavedPaymentAnalyticsFeatureState } from '../selectors/forms';
import { getTotalAmount } from '../selectors/order';
import { getOrderPersistentId } from '../selectors/order/typedOrderSelectors';
import { getSelectedPaymentMethod } from '../selectors/payment';
import { getIsPaymentView } from '../selectors/routing';
import { getStatsigConfigFromState } from '../selectors/statsig';
import { getNumberOfTicketsSelected } from '../selectors/tickets/selected';
import { getHasBogoTickets } from '../selectors/ticketsAndEvent';
import { getLoginFromCheckoutPage } from '../selectors/user';
import { CheckoutState } from '../state';
import { Experiment } from '../state/statsig';
import { trackOrderSuccess } from './eventAnalytics';

export const initializeStatsigSdk = async (
    state: CheckoutState,
    stableID: string | null,
) => {
    const config: StatsigConfiguration = {
        user: getStatsigConfigFromState(state),
    };

    if (stableID) {
        config.options = { overrideStableID: stableID };
    }

    await initializeSdk(config);
};

export const logLoginInterstitialEvent =
    (eventName: string) =>
    (_: Dispatch<CheckoutState>, getState: () => CheckoutState) =>
        logEvent(eventName, null, {
            isInterstitialStep: `${!getLoginFromCheckoutPage(getState())}`,
        });

export const logOrderSuccessEvents =
    () =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const state = getState();
        const orderId = getOrderPersistentId(state);
        const totalCost = getTotalAmount(state);
        const numberOfTickets = getNumberOfTicketsSelected(state);
        const selectedPaymentMethod = getSelectedPaymentMethod(state);

        logEvent(STATSIG_EVENT_NAMES.CHECKOUT_STEP_ORDER_SUCCESS, undefined, {
            ticket_quantity: `${numberOfTickets}`,
            total_cart_value: `${totalCost}`,
            payment_method: `${selectedPaymentMethod}`,
        });

        const checkoutFormValues = state?.form?.[CHECKOUT_FORM_NAME]?.values;
        const ebEmailOptIn =
            checkoutFormValues?.[EB_MARKETING_OPT_IN_FIELD] || false;
        const orgEmailOptIn =
            checkoutFormValues?.[ORG_MARKETING_OPT_IN_FIELD] || false;

        track({
            eventName: HeapEventName.placeOrderSuccess,
            eventData: {
                orderId,
                ebEmailOptIn,
                orgEmailOptIn,
                selectedPaymentMethod,
                savedPayment: getSavedPaymentAnalyticsFeatureState(state),
                totalOrderValue: totalCost,
                has2x1: getHasBogoTickets(state),
            },
        });

        // TODO: to be removed as part of GA deprecation
        dispatch(trackOrderSuccess());
    };

export const updateStatsigUserMetadata: ThunkAction<
    Promise<void>,
    CheckoutState,
    {}
> = async (_dispatch, getState) => {
    const state = getState();
    await updateUser({ user: getStatsigConfigFromState(state) });
};

export enum StatsigActionType {
    saveExperimentData = 'saveExperimentData',
}

export interface SaveExperimentDataAction {
    type: StatsigActionType.saveExperimentData;
    payload: Experiment;
}

export const saveExperimentDataAction = (
    experiment: Experiment,
): SaveExperimentDataAction => ({
    type: StatsigActionType.saveExperimentData,
    payload: experiment,
});

export type StatsigAction = SaveExperimentDataAction;

export const logPaypalPlaceOrderClickInOrderForm = (
    _dispatch: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
) => {
    if (getIsPaymentView(getState())) {
        logEvent(
            STATSIG_EVENT_NAMES.CHECKOUT_STEP_PLACE_ORDER_CLICK,
            undefined,
            { payment_method: PaymentMethodType.NONCE_BRAINTREE },
        );
    }
};
